import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { AuthFrame, authField, authLabel, authButton } from "@/components/admin/AuthFrame";
import { api, apiError } from "@/lib/api";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("The passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame testid="admin-reset-page" eyebrow="Reset password" title="New password">
      {!token ? (
        <p data-testid="reset-missing-token" className="mt-8 border-l-2 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-700">This link is missing its reset code. Please request a new link.</p>
      ) : done ? (
        <div data-testid="reset-done" className="mt-8 border-l-2 border-[#FF5A00] bg-white p-5">
          <CheckCircle2 className="h-6 w-6 text-[#FF5A00]" />
          <p className="mt-3 text-base">Your password has been changed. You can sign in with it now.</p>
          <Link to="/admin/login" data-testid="reset-go-login" className={authButton}>Sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="admin-reset-form">
          <label className={`${authLabel} mt-8`} htmlFor="pw">New password</label>
          <input id="pw" type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className={`${authField} mt-2`} />
          <span className="mt-1.5 block text-xs text-zinc-600">At least 8 characters.</span>
          <label className={`${authLabel} mt-6`} htmlFor="pw2">Repeat new password</label>
          <input id="pw2" type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" className={`${authField} mt-2`} />
          {error && <p data-testid="reset-error" className="mt-4 border-l-2 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <button type="submit" disabled={busy} data-testid="reset-submit" className={authButton}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save new password
          </button>
        </form>
      )}
      {!done && <Link to="/admin/forgot-password" data-testid="reset-request-new" className="mt-6 inline-block font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 underline-offset-4 hover:underline">Request a new link</Link>}
    </AuthFrame>
  );
}
